/**
 * @module features/auth/api/auth-api
 * @description API queries & mutations for Super Admin and Client Portal authentication flows.
 */

import { apiClient, setAccessToken, ApiSuccessEnvelope } from "@/lib/api-client";
import { UserDto, LoginResponseData } from "@/types/auth";

export async function loginWithPassword(params: {
  email: string;
  password: string;
}): Promise<LoginResponseData> {
  const response = await apiClient.post<ApiSuccessEnvelope<LoginResponseData>>(
    "/super-admin/auth/login/",
    {
      email: params.email,
      password: params.password,
    }
  );
  if (response.data.data.access_token) {
    setAccessToken(response.data.data.access_token);
  }
  return response.data.data;
}

export async function verify2FACode(params: {
  temp_token: string;
  code: string;
}): Promise<{ access_token: string; user: UserDto }> {
  const response = await apiClient.post<
    ApiSuccessEnvelope<{ access_token: string; user: UserDto }>
  >("/super-admin/auth/2fa/verify/", params);
  setAccessToken(response.data.data.access_token);
  return response.data.data;
}

export async function loginWithGoogle(
  credential: string
): Promise<{ access_token: string; user: UserDto }> {
  const response = await apiClient.post<
    ApiSuccessEnvelope<{ access_token: string; user: UserDto }>
  >("/super-admin/auth/google/", { credential });
  setAccessToken(response.data.data.access_token);
  return response.data.data;
}

export async function getPasskeyLoginOptions(
  email?: string
): Promise<any> {
  const response = await apiClient.post<ApiSuccessEnvelope<any>>(
    "/super-admin/auth/passkey/login/options/",
    email ? { email } : {}
  );
  return response.data.data;
}

export async function verifyPasskeyLogin(
  credential: any
): Promise<{ access_token: string; user: UserDto }> {
  const response = await apiClient.post<
    ApiSuccessEnvelope<{ access_token: string; user: UserDto }>
  >("/super-admin/auth/passkey/login/verify/", { credential });
  setAccessToken(response.data.data.access_token);
  return response.data.data;
}

export async function refreshSession(): Promise<{
  access_token: string;
  user: UserDto;
}> {
  const response = await apiClient.post<
    ApiSuccessEnvelope<{ access_token: string; user: UserDto }>
  >("/super-admin/auth/refresh/");
  setAccessToken(response.data.data.access_token);
  return response.data.data;
}

export async function logoutUser(): Promise<void> {
  try {
    await apiClient.post("/super-admin/auth/logout/");
  } finally {
    setAccessToken(null);
  }
}

// ---------------------------------------------------------------------------
// Client Portal Authentication APIs (`/api/v1/client/auth/*`)
// ---------------------------------------------------------------------------

export async function clientLoginWithPassword(params: {
  email: string;
  password: string;
}): Promise<LoginResponseData> {
  const response = await apiClient.post<ApiSuccessEnvelope<LoginResponseData>>(
    "/client/auth/login/",
    {
      email: params.email,
      password: params.password,
    }
  );
  if (response.data.data.access_token) {
    setAccessToken(response.data.data.access_token);
  }
  return response.data.data;
}

export async function clientVerify2FACode(params: {
  temp_token: string;
  code: string;
}): Promise<{ access_token: string; user: UserDto }> {
  const response = await apiClient.post<
    ApiSuccessEnvelope<{ access_token: string; user: UserDto }>
  >("/client/auth/2fa/verify/", params);
  setAccessToken(response.data.data.access_token);
  return response.data.data;
}

export async function clientRefreshSession(): Promise<{
  access_token: string;
  user: UserDto;
}> {
  const response = await apiClient.post<
    ApiSuccessEnvelope<{ access_token: string; user: UserDto }>
  >("/client/auth/refresh/");
  setAccessToken(response.data.data.access_token);
  return response.data.data;
}

export async function clientLogoutUser(): Promise<void> {
  try {
    await apiClient.post("/client/auth/logout/");
  } finally {
    setAccessToken(null);
  }
}
